/**
 * CorrespondencePackagePage
 *
 * Package editing experience for correspondence packages.
 * Used within MatterLayout via CorrespondenceRoutes.
 */

import { Loader2 } from 'lucide-react';
import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { CorrespondenceUIProvider } from '@/components/correspondence/contexts/CorrespondenceUIContext';
import { CorrespondencePackageWorkspace } from '@/components/correspondence/CorrespondencePackageWorkspace';
import { Button } from '@/components/ui/button';
import {
  type RecipientEntityType,
  useCorrespondencePackageRetrieveQuery,
} from '@/services/api/correspondenceService';

interface CorrespondencePackagePageProps {
  matterId: string;
}

export const CorrespondencePackagePage: React.FC<
  CorrespondencePackagePageProps
> = ({ matterId }) => {
  const navigate = useNavigate();
  const { package_id: packageId } = useParams<{ package_id: string }>();
  const [searchParams] = useSearchParams();

  const returnUrl =
    searchParams.get('returnUrl') || `/matters/${matterId}/correspondence`;

  const {
    data: packageData,
    isLoading,
    error,
  } = useCorrespondencePackageRetrieveQuery(
    { matterId, id: packageId || '' },
    { skip: !packageId },
  );

  if (!packageId) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-gray-600">Package ID is required</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error && !packageData) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-red-600 mb-2">Failed to load package</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.history.back()}
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  if (!packageData) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const recipientEntityType: RecipientEntityType =
    packageData.recipient_entity_type ?? 'custom';
  const recipientEntityId = packageData.recipient_id ?? '';

  return (
    <div className="h-full">
      <CorrespondenceUIProvider
        matterId={matterId}
        packageId={packageId}
        packageType={packageData.package_type}
        recipientEntityType={recipientEntityType}
        recipientEntityId={recipientEntityId}
      >
        <CorrespondencePackageWorkspace
          onExit={() => navigate(returnUrl)}
          exitIcon="back"
        />
      </CorrespondenceUIProvider>
    </div>
  );
};
